import { Clock, Download, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { getWorkDownloadUrl } from '../../api/workApi'

interface WorkDownloadButtonProps {
  taskId: number
  expiresAt: string
  downloadUrl?: string
}

function getRemainingSeconds(expiresAt: string): number {
  const diff = new Date(expiresAt).getTime() - Date.now()
  return Math.max(0, Math.floor(diff / 1000))
}

function formatCountdown(seconds: number): string {
  if (seconds <= 0) {return 'Expired'}
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) {return `${h}h ${m}m remaining`}
  if (m > 0) {return `${m}m ${s}s remaining`}
  return `${s}s remaining`
}

export default function WorkDownloadButton({ taskId, expiresAt, downloadUrl }: WorkDownloadButtonProps) {
  const [remaining, setRemaining] = useState(() => getRemainingSeconds(expiresAt))
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (remaining <= 0) {return}
    const interval = setInterval(() => {
      setRemaining(getRemainingSeconds(expiresAt))
    }, 1000)
    return () => clearInterval(interval)
  }, [expiresAt, remaining])

  const expired = remaining <= 0

  async function handleDownload() {
    setLoading(true)
    try {
      let url = downloadUrl
      if (!url) {
        const res = await getWorkDownloadUrl(taskId)
        url = res.download_url
      }
      window.open(url, '_blank')
    } catch {
      // silent fail — user can retry
    } finally {
      setLoading(false)
    }
  }

  if (expired) {
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 14px',
          borderRadius: '8px',
          background: 'rgba(107, 114, 128, 0.1)',
          border: '1px solid var(--border-color)',
          color: 'var(--text-muted, #6b7280)',
          fontSize: '13px',
          cursor: 'not-allowed',
        }}
      >
        <XCircle size={14} />
        Link expired
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <button
        onClick={() => { void handleDownload() }}
        disabled={loading}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 16px',
          borderRadius: '8px',
          background: 'var(--primary, #6366f1)',
          color: '#fff',
          border: 'none',
          fontSize: '13px',
          fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.7 : 1,
          transition: 'opacity 0.15s',
        }}
      >
        <Download size={14} />
        {loading ? 'Preparing...' : 'Download'}
      </button>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '11px',
          color: remaining < 3600 ? 'var(--warning, #f59e0b)' : 'var(--text-muted, #6b7280)',
        }}
      >
        <Clock size={11} />
        {formatCountdown(remaining)}
      </div>
    </div>
  )
}
